import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@app/data-layer/enums/documents';
import { CompanyModel } from '@app/data-layer/models/companies';
import { DocumentModel } from '@app/data-layer/models/documents';
import { EntrySummaryModel } from '@app/data-layer/models/entry-summaries';
import { POA_FILE_URL } from '@constants/constants';
import { enumFileExtension } from '@enum/enumFileExtension';
import { enumMimeType } from '@enum/enumMimeType';
import { ReplaySubject } from 'rxjs';
import { DigitalSignsRepository } from '@data-layer/repositories/digital-signs.repository';
import { first } from 'rxjs/operators';

@Component({
  selector: 'app-es-poa-sidebar-old',
  templateUrl: './es-poa-sidebar.component.html',
  styleUrls: ['./es-poa-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsPoaSidebarComponentOld implements OnInit, OnDestroy {
  @Input() public company: CompanyModel;
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public headerTitle: string = 'DOCUMENT_TYPE.POWER_OF_ATTORNEY';
  @Input() public shipmentId: number;
  @Input() public docType: enumDocumentType;

  @Output() public close: EventEmitter<void> = new EventEmitter<void>();
  @Output() public save: EventEmitter<{ file: File; copyPoaToCompany: boolean }> = new EventEmitter<{
    file: File;
    copyPoaToCompany: boolean;
  }>();

  public fileMimeIgnoreList: enumMimeType[] = [enumMimeType.XLS];
  public fileExtensionIgnoreList: enumFileExtension[] = [enumFileExtension.XLS];
  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public POA_FILE_URL: typeof POA_FILE_URL = POA_FILE_URL;
  public dontSaveForFutureFilingsControl: UntypedFormControl;
  public isLoadingDocUpload: boolean;
  public inputFile: File;

  public document: DocumentModel;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private cdr: ChangeDetectorRef, private digitalSignsRepository: DigitalSignsRepository) {}

  ngOnInit(): void {
    this.initializeDocument();

    this.dontSaveForFutureFilingsControl = new UntypedFormControl(false);

    this.initializeDontSaveForFutureFilings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public addDocument(file: File): void {
    this.inputFile = file;
    this.document.state = enumDocumentState.CREATED;
    this.document.fileName = file.name;
    this.document.createdDate = new Date().toDateString();
    this.cdr.markForCheck();
  }

  public removeDocument(): void {
    this.inputFile = null;
    this.initializeDocument();
    this.cdr.markForCheck();
  }

  public onSave() {
    const copyPoaToCompany = !this.dontSaveForFutureFilingsControl.value;
    this.save.emit({
      file: this.inputFile,
      copyPoaToCompany,
    });
  }

  public updateDocumentState(state: enumDocumentState): void {
    this.document.state = state;
  }

  private initializeDocument() {
    this.document = {
      id: -1,
      rootEntityId: this.entrySummary.id,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      relatedEntityId: this.shipmentId,
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      url: null,
      createdDate: null,
      fileName: null,
      type: this.docType,
      state: null,
    };
  }

  private initializeDontSaveForFutureFilings(): void {
    const companyId = this.company?.id;
    if (!companyId) {
      return;
    }

    this.digitalSignsRepository
      .list(companyId)
      .pipe(first())
      .subscribe((items) => {
        if ((items || []).length > 0) {
          this.dontSaveForFutureFilingsControl.setValue(true, { emitEvent: false });
          this.dontSaveForFutureFilingsControl.disable({ emitEvent: false });
          this.cdr.markForCheck();
        }
      });
  }
}
